import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { triggerBlobDownload } from '../../utilities/download';
import { get, uniqueId } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise';
import {
  SdfAlert,
  SdfBadge,
  SdfBusyIndicator,
  SdfButton,
  SdfGrid,
  SdfIcon,
  SdfInput,
  SdfLink,
  SdfMultiSelect,
  SdfPromoBox,
  SdfSelect,
  SdfShimmer,
  SdfTag,
  SdfxPagination
} from '@synerg/react-components';
import Toast from '../common/toast';
import { PAGE_SIZES, ZENDESK_LINK_API_CENTRAL } from '../constants';
import { getFormattedEasternDate } from '../common';
import TooltipComponent from '../common/tooltipComponent';
import { useHistory } from 'react-router-dom';
import PapaParse from 'papaparse';

const agGridKey = window.SynergConfig.getKey('ag-grid-enterprise');
LicenseManager.setLicenseKey(agGridKey);

const AVAILABLE_PAGE_SIZES = [];

export default function SiClients() {
  const gridApiRef = useRef();
  const isInitialRender = useRef(true);
  const prevSortOrderRef = useRef({ colId: 'onBoardingDate', sortBy: 'desc' });
  const [isBusy, setIsBusy] = useState(true);
  const [isDownloadBusy, setIsDownloadBusy] = useState(false);
  const [toast, setToast] = useState(false);
  const [siClients, setSiClients] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [productList, setProductsList] = useState([]);
  const [products, setProducts] = useState([]);
  const [pendingReAuthClients, setPendingReAuthClients] = useState([]);
  const [filterSor, setFilterSor] = useState([]);
  const [filterChange, setFilterChange] = useState(null);
  const [sortOrder, setSortOrder] = useState({ colId: 'onBoardingDate', sortBy: 'desc' });
  const [cancelableRequest, setCancelableRequest] = useState(false);
  const cellStyleClass = "pt-3 text-body-02 leading-xs font-default overflow-ellipsis overflow-hidden";
  const history = useHistory();

  // pagination
  const [selectedPaginationOptions, setSelectedPaginationOptions] = useState({
    totalPages: 1,
    pageSize: 10,
    page: 1
  });
  const [onChangePage, setOnChangePage] = useState(1);
  const [onChangePageSize, setOnChangePageSize] = useState(10);

  const total = isBusy ? 0 : siClients?.pagination?.total || 0;
  const start = total > 0 ? (selectedPaginationOptions.page - 1) * selectedPaginationOptions.pageSize + 1 : 0;
  const end = (selectedPaginationOptions.page * selectedPaginationOptions.pageSize) > total ? total : (selectedPaginationOptions.page * selectedPaginationOptions.pageSize);

  const resetPagination = () => setSelectedPaginationOptions((prev) => {
    return { ...prev, page: 1 }
  });
  const updateFilter = () => resetPagination() || setFilterChange(Date.now());
  const updateFilterSor = v => setFilterSor(v) || updateFilter();

  const removeTag = (tag) => {
    const updatedStatus = filterSor.filter((status) => status !== tag);
    updateFilterSor(updatedStatus);
    onChangePage !== 1 && setOnChangePage(1);
  };

  const clearAllButton = () => {
    resetPagination();
    updateFilterSor([]);
    onChangePage !== 1 && setOnChangePage(1);
  };

  const hideTable = () => {
    return isBusy ? 'hidden' : ' ';
  };

  const handleResponse = (message, status = "error") => {
    setToast({
      key: uniqueId(),
      status: status,
      message: message,
      showToast: true,
    });
  };

  const getCancelableRequestToken = () => {
    if (cancelableRequest) {
      cancelableRequest.cancel('search request canceled');
    }
    const newRequest = axios.CancelToken.source();
    setCancelableRequest(newRequest);
    return newRequest;
  };

  const getSearchParams = (currPage = 1, pageSize = 10, searchTerm, sort = 'onBoardingDate', order = 'desc', sorFilters = []) => {
    const params = {
      page: currPage,
      pageSize: pageSize,
      search: searchTerm,
      colName: sort,
      order: order,
      filters: sorFilters
    };

    // remove non-zero falsey params
    return Object.keys(params).reduce((prev, key) => {
      if (params[key] || params[key] === 0) {
        prev[key] = params[key];
      }
      return prev;
    }, {});
  };

  const getSiClients = async () => {
    try {
      setIsBusy(true);
      const axiosInstance = axios.create();
      const { data } = await axiosInstance.get(`${import.meta.env.VITE_ROOT_PATH}/api/si/clients`, {
        cancelToken: getCancelableRequestToken().token,
        params: getSearchParams(selectedPaginationOptions.page,
          selectedPaginationOptions.pageSize,
          searchTerm,
          sortOrder.colId,
          sortOrder.sortBy || 'desc',
          filterSor.map(item => item.value)
        )
      });
      setSiClients(data);
      if (data?.pagination) {
        setSelectedPaginationOptions((prev) => {
          return { ...prev, totalPages: Math.ceil(data.pagination.total / parseInt(data.pagination.pageSize)), page: data.pagination.page }
        });
      }
      setIsBusy(false);

    } catch (e) {
      if (!axios.isCancel(e)) {
        const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching client details. Please try again.');
        handleResponse(errorMessage);
        setSiClients({ data: [] });
      }
      setIsBusy(false);
    }
  };

  const downloadReport = async () => {
    try {
      setIsDownloadBusy(true);
      const response = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/si/clients`, {
        cancelToken: getCancelableRequestToken().token,
        params: getSearchParams(1,
          siClients?.pagination?.total || 1000,
          searchTerm,
          sortOrder.colId,
          sortOrder.sortBy || 'desc',
          filterSor.map(item => item.value)
        )
      });

      if (response.status === 200) {
        const data = get(response, 'data.data', []);
        const dataRowArr = [];
        for (let row of data) {
          const productItem = products?.find((product) => row?.sor === product?.shortName);
          dataRowArr.push({
            'Client Name': row.name || 'NA',
            'OOID': get(row, 'ooid', 'NA'),
            'Purchase date': get(row, 'onBoardingDate', 'NA') ? getFormattedEasternDate(row.onBoardingDate) : 'NA',
            'Use Case': get(row, 'useCase', 'NA'),
            'ADP Product': get(productItem, 'name', 'NA')
          });
        }
        if (dataRowArr.length < 1) {
          setIsDownloadBusy(false);
          handleResponse("No clients available for download!");
        } else {
          const jsonToCSV = PapaParse.unparse(dataRowArr);
          const BOM = '\uFEFF';
          const blob = new Blob([BOM + jsonToCSV], { type: 'text/csv;charset=utf-8'});
          triggerBlobDownload(blob, 'Accelerator Clients.csv');
          setIsDownloadBusy(false);
        }
      }
      else {
        handleResponse('Error occurred while downloading report! Please try again.');
        setIsDownloadBusy(false);
      }

    } catch (e) {
      handleResponse('Error occurred while downloading report! Please try again.');
      setIsDownloadBusy(false);
    }
  };

  useEffect(() => {
    const getListOfProducts = async () => {
      const { data: products } = await axios.get(
        `${import.meta.env.VITE_ROOT_PATH}/api/products`
      );
      setProducts(products);
      setProductsList(products.map(item => ({ 'label': item.name, 'value': item.shortName })));
    };
    const getReAuthRequiredClients = async () => {
      const { data } = await axios.get(
        `${import.meta.env.VITE_ROOT_PATH}/api/si/clients/reauth`
      );
      setPendingReAuthClients(data);
    };

    getListOfProducts();
    getReAuthRequiredClients();

  }, []);


  const myIcons = {
    first: "<sdf-icon icon='nav-page-first'></sdf-icon>",
    previous: "<sdf-icon icon='nav-page-back'></sdf-icon>",
    next: "<sdf-icon icon='nav-page-next'></sdf-icon>",
    last: "<sdf-icon icon='nav-page-last'></sdf-icon>",
    sortAscending: "<sdf-icon icon='action-sort-asc'></sdf-icon>",
    sortDescending: "<sdf-icon icon='action-sort-desc'></sdf-icon>",
    sortUnSort: "<sdf-icon icon='action-sort'></sdf-icon>"
  };

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: false,
    wrapText: true,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    unSortIcon: true,
    cellDataType: false
  };

  const gridOptions = {
    defaultColDef: defaultColDef,
    pagination: false,
    suppressPaginationPanel: true,
    paginationPageSize: 10,
    suppressHorizontalScroll: true
  };

  const determineSortOrder = (colId) => {
    if (sortOrder.colId === colId) {
      return sortOrder.sortBy === 'asc' ? 'asc' : 'desc';
    }
    return null;
  };

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: 'Client name',
      headerClass: "bg-white text-heading-01 font-bold",
      cellStyle: {
        whiteSpace: "nowrap",
        overflow: "visible",
        textOverflow: 'ellipsis'
      },
      cellRenderer: (params) => {
        return ClientInfo(params.data);
      },
      flex: '7',
      minWidth: 240,
      sortable: true,
      sort: determineSortOrder('name'),
      comparator: () => 0
    },
    {
      field: 'ooid',
      headerName: 'OOID',
      headerClass: "bg-white text-heading-01 font-bold",
      cellRenderer: (params) => {
        return <div className={cellStyleClass}>{params.data.ooid}</div>
      },
      cellStyle: {
        fontWeight: 450,
      },
      flex: '4',
      sortable: false
    },
    {
      field: 'onBoardingDate',
      headerName: 'Purchase date',
      headerClass: "bg-white text-heading-01 font-bold",
      cellRenderer: (params) => {
        return <div className={cellStyleClass}>{getFormattedEasternDate(params.data.onBoardingDate)}</div>
      },
      cellStyle: {
        fontWeight: 450,
      },
      flex: '3',
      sortable: true,
      sort: determineSortOrder('onBoardingDate'),
      comparator: () => 0
    },
    {
      field: 'useCase',
      headerName: 'Use Case',
      headerClass: "bg-white text-heading-01 font-bold",
      cellRenderer: (params) => UseCaseCell(params),
      cellStyle: {
        fontWeight: 450,
        whiteSpace: "nowrap",
        overflow: "visible",
        textOverflow: 'ellipsis'
      },
      flex: '3',
      sortable: true,
      sort: determineSortOrder('useCase'),
      comparator: () => 0
    },
    {
      field: 'sor',
      headerName: 'ADP Product',
      headerClass: "bg-white text-heading-01 font-bold",
      cellRenderer: (params) => {
        const productItem = products?.find((product) => params.data.sor === product?.shortName);
        return <div className={cellStyleClass}>{productItem?.name || 'NA'}</div>
      },
      cellStyle: {
        fontWeight: 450,
      },
      flex: '3',
      sortable: true,
      sort: determineSortOrder('sor'),
      comparator: () => 0
    },
    {
      field: '',
      headerClass: "bg-white text-heading-01 font-bold",
      cellRenderer: (params) => View(params),
      flex: '2',
      minWidth: 80,
      sortable: false
    }
  ], [sortOrder, products]);


  useEffect(() => {
    if ((onChangePage !== selectedPaginationOptions.page || onChangePageSize !== selectedPaginationOptions.pageSize) && filterChange === null) {
      return;
    }

    const sortChanged = prevSortOrderRef.current.colId !== sortOrder.colId ||
      prevSortOrderRef.current.sortBy !== sortOrder.sortBy;

    if (sortChanged || filterChange || onChangePage || onChangePageSize) {
      getSiClients();
    }

    prevSortOrderRef.current = sortOrder;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangePage, onChangePageSize, sortOrder, filterChange]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    let searchTimer;
    setIsBusy(true);
    searchTimer = setTimeout(() => {
      getSiClients();
    }, 2000);

    return () => {
      clearTimeout(searchTimer);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);


  //For getting Available Page Sizes according to the number of fetched records
  useEffect(() => {
    if (siClients?.data?.length > 0) {
      AVAILABLE_PAGE_SIZES.length = 0;
      if (siClients?.pagination?.total) {
        PAGE_SIZES.every((element) => {
          if (siClients.pagination.total > element.value) {
            AVAILABLE_PAGE_SIZES.push(element);
            return true;
          }
          else {
            AVAILABLE_PAGE_SIZES.push(element);
            return false;
          }
        })
      }
    }
  }, [siClients]);


  const ClientInfo = (rowData) => {
    let badgeStatus;
    let badgeText;

    if (rowData.hasOwnProperty("isClientInAPIC") && !rowData.isClientInAPIC) {
      badgeStatus = 'failed';
      badgeText = 'Needs API Central';
    }
    else {
      if (rowData.status === "PARTNER_APP_SUBSCRIBED") {
        badgeStatus = 'new';
        badgeText = 'New';
      }
      else if (rowData.status === "CLIENT_APP_SUBSCRIBED" || rowData.changeRequests.length > 0) {
        badgeStatus = 'changed';
        badgeText = 'Pending Approval';
      }
      else if (rowData.status === "COMPLETE" && rowData.changeRequests.length <= 0) {
        badgeStatus = 'complete';
        badgeText = 'Approved';
      }
      else {
        badgeStatus = "";
        badgeText = "";
      }
    }

    return (
      <div className='pt-2 w-full flex col-gap-4 justify-between'>
        <TooltipComponent content={rowData.name || ''} direction="right">
          <div className="pt-1 truncate font-default text-heading-02 font-bold" style={{ width: "150px" }} >
            {rowData.name || ''}
          </div>
        </TooltipComponent>

        {badgeStatus && badgeText && <SdfBadge className='ml-auto h-full text-right pr-3' status={badgeStatus}>{badgeText}</SdfBadge>}
      </div>
    )
  };

  const UseCaseCell = (params) => {
    return (
      <TooltipComponent content={(params?.data?.useCase?.charAt(0).toUpperCase() + params?.data?.useCase?.slice(1)) || 'NA'} direction="right">
        <div className={`truncate ${cellStyleClass}`} style={{ width: "130px" }} >
          {(params?.data?.useCase?.charAt(0).toUpperCase() + params?.data?.useCase?.slice(1)) || 'NA'}
        </div>
      </TooltipComponent>
    )
  };

  const View = (params) => {
    return (
      <div className={`${cellStyleClass} text-alert-info hover:underline`} onClick={() => {
        history.push({
          pathname: `/accelerator-projects/${get(params, 'data.ooid', '')}`, state: {
            currClientOOID: get(params, 'data.ooid', ''),
            currClientStatus: get(params, 'data.status', ''),
            currClientId: get(params, 'data.clientId', ''),
            currPartnerId: get(params, 'data.partnerId', '')
          }
        });
      }}>
        View
      </div>
    );
  };

  useEffect(() => {
    if ((onChangePage !== selectedPaginationOptions.page || onChangePageSize !== selectedPaginationOptions.pageSize) && filterChange === null) {
      return;
    }
    getSiClients();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangePage, onChangePageSize, sortOrder, filterChange]);

  return (
    <div className="mx-24 my-10">
      {siClients?.newClientsCount > 0 ? <SdfAlert
        status="info"
        closeable={true}
        toast={true}
        style={{ display: "block !important" }}
        autoClose={false}
      >
        <div className="flex justify-between w-full leading-xl">
          <span className=''>Good news! You gained {siClients.newClientsCount} new {siClients.newClientsCount > 1 ? 'clients' : 'client'}. Let's create a project for them.</span>
        </div>
      </SdfAlert> : <></>}

      {pendingReAuthClients.length > 0 && <SdfAlert
        status="info"
        closeable={true}
        toast={true}
        style={{ display: "block !important" }}
        autoClose={false}
      >
        <SdfIcon slot='icon' icon="status-flagged" className="text-alert-error" />
        <div className="flex justify-between w-full leading-xl">
          <span className=''>Consent re-authorization for newly added APIs is pending for {pendingReAuthClients.length} client(s). Once approved, you can begin using those additional APIs.</span>
        </div>
      </SdfAlert>}

      {toast && <Toast
        key={toast.key}
        status={toast.status}
        message={toast.message}
        showToast={toast.showToast}
      />}

      <div slot="breadcrumb" className="mb-5" onClick={() => { }}>
        Accelerator clients <span>/</span>
      </div>
      <div className={"flex pb-5 mx-0 justify-between"}>
        <span className={"text-heading-07 font-black"}>Accelerator clients</span>
        <div>
          <SdfButton
            emphasis="secondary"
            onClick={downloadReport}
            disabled={!(siClients?.pagination?.total) || isDownloadBusy || isBusy ? true : null}
          >
            <div style={{ height: '1.625rem' }}>
              Download report
              {isDownloadBusy && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
            </div>
          </SdfButton>
        </div>
      </div>
      <SdfPromoBox
        size='md'
        layout='horizontal'
        align='left'
        variant='shadow'
        header='Need help?'
        message='For support with your API Central Accelerator clients please click here to contact your API Central support team.'
        illustration-name={'chat'}
      >
        <SdfLink emphasis="tertiary" size="sm" slot="cta" href={ZENDESK_LINK_API_CENTRAL} target="_blank">Contact support</SdfLink>
      </SdfPromoBox>

      <div className={"bg-action-default-50 my-5 rounded-xl"}>
        <div className={"pt-10 pb-2 px-12 flex justify-between gap-1"}>
          <SdfInput
            className={"mr-6 w-4/5"}
            label={"Search"}
            placeholder={"Search by client name or OOID"}
            onSdfInput={($event) => {
              setSelectedPaginationOptions((prev) => {
                return { ...prev, pageSize: 10, page: 1 }
              });
              setSearchTerm($event.detail || '');
            }}
          >
            <SdfIcon slot={"after"} icon={"action-search"} />
          </SdfInput>

          <div className="textbox-with-label">
            <SdfMultiSelect
              label={"Filter by ADP product"}
              className="min-w-60"
              items={productList}
              value={filterSor}
              placeholder="Select product"
              input_width={"xl"}
              onSdfChange={({ detail }) => {
                updateFilterSor(detail);
                setOnChangePage(1);
              }}
            />
          </div>
        </div>

        <div className={"flex flex-wrap tags-section pl-12 pb-10"}>
          {filterSor.map((sor, idx) => (
            <SdfTag
              className="bg-white rounded-full mr-2 mt-4"
              onClick={() => removeTag(sor)}
              key={`tag-${idx}`}
              value={sor.label}
            />
          ))}
          {filterSor.length > 0 &&
            <SdfButton onClick={clearAllButton} className={'ml-2 mt-4'} variant={'text'}>Clear all</SdfButton>
          }
        </div>
      </div>

      <div>
        <div className={"flex mt-8 mb-4 mx-0 justify-between"}>
          <div className={"block w-lg text-3xl font-extrabold leading-xs tracking-xs"}>Results ({total})</div>
        </div>

        {<SdfGrid className={"ag-theme-quartz " + hideTable()}>
          <AgGridReact
            ref={gridApiRef}
            overlayNoRowsTemplate={"No clients to show"}
            rowHeight={59}
            headerHeight={59}
            domLayout='autoHeight'
            rowClass="pt-1 cursor-pointer"
            icons={myIcons}
            rowData={siClients.data || []}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            autoSizeAllColumns={true}
            onSortChanged={($event) => {
              if ($event.source === 'uiColumnSorted') {
                const columnState = $event.api.getColumnState();
                const sortedColumns = columnState.filter(col => col.sort != null);

                if (sortedColumns.length > 0) {
                  const { colId, sort } = sortedColumns[0];
                  if (colId !== sortOrder.colId || sort !== sortOrder.sortBy) {
                    setSelectedPaginationOptions((prev) => ({ ...prev, page: 1 }));
                    setSortOrder({ colId: colId, sortBy: sort });
                    setOnChangePage(1);
                  }
                } else {
                  // No sort - revert to default
                  const defaultSort = { colId: 'onBoardingDate', sortBy: 'desc' };
                  if (sortOrder.colId !== defaultSort.colId || sortOrder.sortBy !== defaultSort.sortBy) {
                    setSelectedPaginationOptions((prev) => ({ ...prev, page: 1 }));
                    setSortOrder(defaultSort);
                    setOnChangePage(1);
                  }
                }
              }
            }}
            onFirstDataRendered={(params) => {
              if (params.api && sortOrder.colId === 'onBoardingDate' && sortOrder.sortBy === 'desc') {
                params.api.applyColumnState({
                  state: params.api.getColumnState().map(col => ({ ...col, sort: null })),
                  defaultState: { sort: null }
                });
              }
            }}
          />
        </SdfGrid>
        }

        <div className={"flex-col items-center gap-8"}>
          {isBusy && (<SdfShimmer rows={10} />)}
        </div>
        <div className="flex items-center justify-end pt-4 pb-20">
          <div className="text-sm text-action-default">Rows per page:</div>
          <SdfSelect
            className="w-20 text-sm text-action-default px-4 mb-1 border-r-1 border-t-0 border-l-0 border-b-0 border-dark border-solid"
            label="rows per page"
            hideLabel={true}
            items={AVAILABLE_PAGE_SIZES}
            onSdfChange={({ detail }) => {
              setSelectedPaginationOptions((prev) => {
                return { ...prev, page: 1, pageSize: detail.value }
              });
              setOnChangePageSize(detail.value);
              setOnChangePage(1);
            }}
            value={{ label: `${selectedPaginationOptions.pageSize}`, value: selectedPaginationOptions.pageSize }}
          />
          <div className="text-sm text-action-default px-4">{`${start} - ${end} items`}</div>
          {selectedPaginationOptions.totalPages >= 1 &&
            <SdfxPagination
              currentPage={selectedPaginationOptions.page || 1}
              totalPages={selectedPaginationOptions.totalPages || 0}
              onSdfxPageChange={({ detail }) => {
                setSelectedPaginationOptions((prev) => {
                  return { ...prev, page: detail }
                })
                setOnChangePage(detail);
              }}
            />
          }
        </div>
      </div>
    </div>
  );
}
